import type { ComponentType } from "react"

import { BrowserInjectedIcon } from "@/domains/icons/huge-icons/monotone/browser-injected"
import { CoinbaseIcon } from "@/domains/icons/huge-icons/monotone/coinbase"
import { MetaMaskIcon } from "@/domains/icons/huge-icons/monotone/metamask"
import { RabbyIcon } from "@/domains/icons/huge-icons/monotone/rabby"
import { WalletIcon } from "@/domains/icons/huge-icons/monotone/wallet"
import { WalletConnectIcon } from "@/domains/icons/huge-icons/monotone/wallet-connect"

export type ConnectorInfo = {
  id: string
  name: string
  type?: string
}

/**
 * Maps a connector to its corresponding icon component.
 * This centralizes the logic for determining which wallet icon to display.
 */
export function getConnectorIcon(connector: ConnectorInfo | null | undefined): ComponentType<any> {
  if (!connector) {
    return WalletIcon
  }

  const id = connector.id.toLowerCase()
  const name = connector.name.toLowerCase()

  // Check name first for better detection of injected wallets
  if (name.includes("rabby")) {
    return RabbyIcon
  }
  if (name.includes("coinbase")) {
    return CoinbaseIcon
  }
  if (name.includes("walletconnect")) {
    return WalletConnectIcon
  }
  if (name.includes("metamask")) {
    return MetaMaskIcon
  }

  // Fallback to ID checks
  if (id.includes("coinbase")) {
    return CoinbaseIcon
  }
  if (id.includes("rabby")) {
    return RabbyIcon
  }
  if (id.includes("walletconnect")) {
    return WalletConnectIcon
  }
  if (id.includes("metamask")) {
    return MetaMaskIcon
  }

  // For injected connector (browser extension), use browser icon
  if (id.includes("injected") || connector.type === "injected") {
    return BrowserInjectedIcon
  }

  // Default fallback
  return WalletIcon
}

/**
 * Maps a connector to its display name.
 * This centralizes the logic for determining which wallet name to display.
 */
export function getConnectorDisplayName(connector: ConnectorInfo | null | undefined): string {
  if (!connector) {
    return "Wallet"
  }

  const id = connector.id.toLowerCase()
  const name = connector.name.toLowerCase()

  // Check name first for better detection of injected wallets
  if (name.includes("rabby")) {
    return "Rabby"
  }
  if (name.includes("coinbase")) {
    return "Coinbase Wallet"
  }
  if (name.includes("walletconnect")) {
    return "WalletConnect"
  }
  if (name.includes("metamask")) {
    return "MetaMask"
  }
  if (name.includes("trust")) {
    return "Trust Wallet"
  }
  if (name.includes("rainbow")) {
    return "Rainbow"
  }

  // Fallback to ID checks
  if (id.includes("coinbase")) {
    return "Coinbase Wallet"
  }
  if (id.includes("rabby")) {
    return "Rabby"
  }
  if (id.includes("walletconnect")) {
    return "WalletConnect"
  }
  if (id.includes("metamask")) {
    return "MetaMask"
  }
  if (id.includes("trust")) {
    return "Trust Wallet"
  }
  if (id.includes("rainbow")) {
    return "Rainbow"
  }

  // Use the connector's name if available, otherwise fallback
  return connector.name || "Wallet"
}
